import { useCreateTicket } from "../hooks/useCreateTicket";
import { useUploadFichiers } from "../hooks/useUploadFichiers";
import { getTypesTicket } from "../lib/ticket";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useDropzone } from "react-dropzone";
import { FaPlus, FaPaperclip } from "react-icons/fa";

const CreateTicketModal = ({ onClose, onSubmitSuccess, onError }) => {

  const { uploadFichiers, loading: uploading } = useUploadFichiers();
  const [fichiers, setFichiers] = useState([]);
  const [demandeExterneId, setDemandeExterneId] = useState(null);
  const [telephone, setTelephone] = useState("");
  const [title, setTitle] = useState("");
  const { userInfo } = useAuth();

  const onDrop = useCallback((acceptedFiles) => {
    setFichiers(prev => [...prev, ...acceptedFiles]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleTelephoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').substring(0, 10);
    setTelephone(value);
  };

  const { create, loading } = useCreateTicket();

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const data = await getTypesTicket();
        const demandeExterne = data.find(type => type.label === "Demande externe");
        setDemandeExterneId(demandeExterne ? demandeExterne.id : (data[0]?.id || 1));
      } catch {
        setDemandeExterneId(1);
      }
    };
    fetchTypes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    if (title.trim() === "") {
      onError("Le titre ne doit pas être vide");
      return;
    }
    if (telephone.length < 10) {
      onError("Le numéro de téléphone doit faire 10 chiffres");
      return;
    }

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const userName = userInfo?.userName || "Utilisateur";
    const formattedTelephone = telephone.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    const titreFormate = `${userName} - ${formattedTelephone} - ${title.trim()}`;

    const idClient = localStorage.getItem("client_id");

    const created = await create({
      idClient: idClient || 2,
      titre: titreFormate,
      description: form.descriptionTicketAdd.value,
      priorite: parseInt(form.prioriteTicketAdd.value),
      idType: demandeExterneId,
      dateDebut: `${dateStr} ${timeStr}`,
    });

    if (created) {
      if (fichiers.length > 0) {
        uploadFichiers(created.idTicket, fichiers);
      }
      onClose();
      onSubmitSuccess();
    } else {
      onError();
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
        <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
          <div className="modal-content shadow-lg">
            <form onSubmit={handleSubmit}>
              <div className="modal-header bg-light border-bottom">
                <h5 className="modal-title font-weight-bold">
                  <FaPlus className="me-2 text-primary" />
                  Nouveau ticket
                </h5>
                <button type="button" className="btn-close" onClick={onClose} aria-label="Fermer" />
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label className="font-weight-semibold">
                    Titre <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="form-control"
                    name="titreTicketAdd"
                    placeholder="Résumé du problème..."
                    required
                    maxLength={50}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group col-md-7">
                    <label className="font-weight-semibold">
                      Téléphone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      value={telephone}
                      onChange={handleTelephoneChange}
                      placeholder="0612345678"
                      required
                    />
                  </div>
                  <div className="form-group col-md-5">
                    <label className="font-weight-semibold">Priorité</label>
                    <select className="form-control" name="prioriteTicketAdd">
                      <option value="0">Normal</option>
                      <option value="1">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="font-weight-semibold">Description</label>
                  <textarea
                    className="form-control"
                    name="descriptionTicketAdd"
                    rows={4}
                    placeholder="Décrivez votre problème..."
                  />
                </div>

                <div
                  {...getRootProps()}
                  className={`border rounded p-3 text-center${isDragActive ? " border-primary bg-light" : " border-secondary"}`}
                  style={{ cursor: "pointer", borderStyle: "dashed" }}
                >
                  <input {...getInputProps()} />
                  <FaPaperclip className="me-2 text-muted" />
                  {isDragActive
                    ? <span className="text-primary">Déposez les fichiers ici...</span>
                    : <span className="text-muted small">Glissez-déposez des fichiers, ou cliquez pour en sélectionner</span>
                  }
                  {fichiers.length > 0 && (
                    <ul className="list-unstyled mt-2 mb-0 text-left">
                      {fichiers.map((file, index) => (
                        <li key={index} className="small text-dark">
                          <FaPaperclip className="me-1 text-secondary" />{file.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="modal-footer bg-light border-top">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  data-testid="create-ticket-button-modal"
                >
                  {loading ? "Création..." : "Créer le ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default CreateTicketModal;
